import {
  ArrowRight,
  BarChart3,
  BookOpen,
  CheckCircle2,
  FlaskConical,
  GitBranch,
  Scale,
  ShieldCheck,
  Sigma,
} from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import experimentsJson from "../data/botExperiments.generated.json";
import {
  type BotArticle,
  botArticleById,
  botArticles,
  measuredExperimentIds,
} from "../data/botResearch";
import "../styles/blog.css";

type ExperimentResult = {
  game: string;
  profileA: string;
  profileB: string;
  games: number;
  winsA: number;
  winsB: number;
  draws: number;
  winRateA: number;
  winRateB: number;
  drawRate: number;
  winRateA95: [number, number];
  winRateB95: [number, number];
  notes: string[];
  metrics?: Record<string, number>;
};

const experimentResults = (experimentsJson.results as unknown as ExperimentResult[]) ?? [];
const connectDiagramCells = Array.from({ length: 28 }, (_, index) => ({
  id: `connect-cell-${index + 1}`,
  tone:
    index === 17 || index === 18 || index === 19
      ? "red"
      : index === 24 || index === 25
        ? "yellow"
        : "",
}));
const battleshipDiagramCells = Array.from({ length: 64 }, (_, index) => ({
  id: `battleship-cell-${index + 1}`,
  target: index === 35,
  opacity: index === 35 ? 1 : 0.16 + ((index * 17) % 11) / 14,
}));
const ticTacToeDiagramCells = [
  { id: "top-left", mark: "X", tone: "x" },
  { id: "top-middle", mark: "O", tone: "o" },
  { id: "top-right", mark: "O", tone: "o" },
  { id: "middle-left", mark: "", tone: "" },
  { id: "center", mark: "X", tone: "x" },
  { id: "middle-right", mark: "", tone: "" },
  { id: "bottom-left", mark: "", tone: "" },
  { id: "bottom-middle", mark: "", tone: "" },
  { id: "bottom-right", mark: "", tone: "winning-option" },
];
const percent = (value: number) => `${(value * 100).toFixed(1)}%`;
const profileLabel = (value: string) =>
  value
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

// A policy keeps the same colour in every article and every head-to-head chart.
// This makes the outcome bars readable as a shared visual language rather than
// assigning meaning based on whether a policy happened to be listed first.
const policyColors: Record<string, string> = {
  random: "#8fa6c2",
  easy: "#2ed69b",
  medium: "#2f8cff",
  hard: "#ff647c",
  aggressive: "#ff8a3d",
  defensive: "#2ccaf5",
  strategic: "#ffc247",
  balanced: "#14b8a6",
  hunt: "#06b6d4",
  probability: "#3b82f6",
  "probability-defensive": "#f59e0b",
  uniform: "#8fa6c2",
  frequency: "#2f8cff",
  transition: "#ff8a3d",
};
const policyColor = (policy: string) => policyColors[policy] ?? "#7dd3fc";

const experimentPolicyContexts: Record<string, Record<string, string>> = {
  "tic-tac-toe": {
    random:
      "Random chooses any empty square with equal probability. It does not look for wins, blocks, or patterns, so it is the control opponent.",
    easy: "Easy follows a fixed legal-square order. It is predictable practice and never searches future replies.",
    medium:
      "Medium first takes an immediate win, then blocks an immediate loss, then prefers the centre and corners. It is tactical but does not solve the full game tree.",
    hard: "Hard searches every legal continuation with minimax. It assumes the opponent makes their strongest reply and therefore cannot be forced to lose.",
  },
  chess: {
    random:
      "Random samples uniformly from legal moves. It is a baseline, not a realistic chess style, and gives no special value to material or king safety.",
    aggressive:
      "Aggressive rewards checks, captures, advanced pawns, and restricting the opponent, while still rejecting illegal or tactically losing moves found by its search.",
    defensive:
      "Defensive gives extra weight to king safety, castling, development, material preservation, and reducing the opponent's mobility.",
    strategic:
      "Strategic values centre control, developed bishops and knights, sustainable pawn progress, and the longer-term position rather than only immediate forcing moves.",
  },
  ludo: {
    easy: "Easy takes the first legal goti move in a repeatable order. It follows every rule but does not compare capture, safety, or endgame value.",
    medium:
      "Medium uses the production balanced difficulty: it weighs progress, captures, safety, and releasing gotis without the full hard-mode tactical scoring.",
    hard: "Hard scores every legal goti using captures, threatened-goti rescue, home progress, blockades, exposure, and endgame pressure.",
    balanced:
      "Balanced trades off safe progress, escaping threats, captures, and bringing more gotis into play, with no single feature dominating every decision.",
    aggressive:
      "Aggressive gives the largest rewards to captures, pursuit distance, pressure, and extra-turn opportunities, accepting more exposure to create attacks.",
    defensive:
      "Defensive prioritises safe squares, blockades, escaping threats, and low exposure, even when a riskier move would advance farther.",
  },
  "connect-four": {
    random:
      "Random chooses uniformly from non-full columns and does not recognise threats or connected lines.",
    easy: "Easy uses a repeatable column preference and basic legal play without searching the opponent's future replies.",
    medium:
      "Medium detects immediate wins and blocks, prefers central columns, and searches a limited number of replies for short tactical threats.",
    hard: "Hard searches more deeply with minimax and scores open two, three, and four-cell windows, including threats the opponent can force next.",
  },
  battleship: {
    random:
      "Random fires uniformly at any untried cell. It remembers previous shots only so it never wastes a turn on the same cell.",
    hunt: "Hunt spreads search shots across the board, then targets neighbouring cells after a hit and follows the discovered ship direction.",
    probability:
      "Probability counts every remaining ship placement consistent with public hits and misses, then attacks a cell covered by the most possibilities.",
    "probability-defensive":
      "Probability plus defence uses the same evidence-based attack policy and also spaces and rotates its own fleet to reduce predictable clusters.",
  },
};

function articleStyle(article: BotArticle): CSSProperties {
  return { "--lab-accent": article.accent, "--lab-accent-2": article.accent2 } as CSSProperties;
}

export function BlogIndexPage() {
  return (
    <section className="blog-page blog-index">
      <header className="blog-hero">
        <span className="eyebrow">TOPZLE LAB</span>
        <h1>Games, bots, and the evidence behind them</h1>
        <p>
          Understand each game from its rules to its decision model, then inspect the measured
          studies, uncertainty, and limitations behind every result we publish.
        </p>
      </header>
      <div className="blog-feature-grid">
        <Link to="/blog/smart-ludo-dice" className="blog-feature-card">
          <Scale />
          <span>
            <small>RULES & FAIRNESS</small>
            <strong>How Smart Dice works in Ludo</strong>
          </span>
          <ArrowRight />
        </Link>
        <Link to="/blog/bot-experiments" className="blog-feature-card">
          <FlaskConical />
          <span>
            <small>RESEARCH METHOD</small>
            <strong>How we compare game bots</strong>
          </span>
          <ArrowRight />
        </Link>
      </div>
      <h2>Bot field notes</h2>
      <div className="blog-card-grid">
        {botArticles.map((article) => (
          <article key={article.id} className="blog-card" style={articleStyle(article)}>
            <BookOpen />
            <h3>{article.name}</h3>
            <p>{article.abstract}</p>
            <div className="blog-card-links">
              <Link to={`/blog/bots/${article.id}`}>Bot details</Link>
              {measuredExperimentIds.has(article.id) ? (
                <Link to={`/blog/experiments/${article.id}`}>Measured study</Link>
              ) : null}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export function BotArticlePage() {
  const { gameId = "" } = useParams();
  const article = botArticleById.get(gameId);
  if (!article) return <BlogMissing />;
  return (
    <article className="blog-page blog-article research-paper" style={articleStyle(article)}>
      <PaperHeader eyebrow="BOT SYSTEM NOTE" title={`How the ${article.name} bot thinks`}>
        {article.abstract}
      </PaperHeader>

      <PaperSection number="01" title="The game before the bot" icon={<BookOpen />}>
        <p>
          A strategy only makes sense after the legal game is defined. These are the rules and
          information boundaries used by the bot.
        </p>
        <ol className="rule-list">
          {article.gameWorks.map((rule) => (
            <li key={rule}>{rule}</li>
          ))}
        </ol>
        <GameDiagram gameId={article.id} />
        <CitationLine references={article.references} />
      </PaperSection>

      <PaperSection number="02" title="Terms used in this article" icon={<BookOpen />}>
        <div className="term-grid">
          {article.terms.map((item) => (
            <div className="term-card" key={item.term}>
              <dfn>{item.term}</dfn>
              <p>{item.definition}</p>
            </div>
          ))}
        </div>
      </PaperSection>

      <PaperSection number="03" title="From game state to one legal action" icon={<GitBranch />}>
        <DecisionPipeline />
        <p>
          Rules validation and strategy are separate. The game engine first exposes legal actions. A
          policy may score or search only those actions. The chosen event is then sent through the
          same reducer used for a human move, so the bot cannot bypass the rules.
        </p>
      </PaperSection>

      <PaperSection number="04" title="Bot personalities and decision rules" icon={<BarChart3 />}>
        <div className="profile-grid">
          {article.profiles.map((profile, index) => (
            <article className="profile-card" key={profile.name}>
              <span className="profile-index">P{index + 1}</span>
              <h3>{profile.name}</h3>
              <p>{profile.behaviour}</p>
              <code>{profile.scoring}</code>
            </article>
          ))}
        </div>
        <CitationLine references={article.references} />
      </PaperSection>

      <PaperSection number="05" title="The mathematics, symbol by symbol" icon={<Sigma />}>
        <div className="equation-stack">
          {article.equations.map((equation) => (
            <article className="equation-card" key={equation.title}>
              <h3>{equation.title}</h3>
              <div className="formula" role="math" aria-label={equation.formula}>
                {equation.formula}
              </div>
              <p>{equation.explanation}</p>
              <ul className="symbol-list">
                {equation.symbols.map((symbol) => (
                  <li key={symbol}>{symbol}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </PaperSection>

      <PaperSection number="06" title="Worked decision example" icon={<CheckCircle2 />}>
        <div className="worked-example">{article.workedExample}</div>
      </PaperSection>

      <PaperSection number="07" title="What this bot does not prove" icon={<ShieldCheck />}>
        <ul className="limitation-list">
          {article.limitations.map((limitation) => (
            <li key={limitation}>{limitation}</li>
          ))}
        </ul>
      </PaperSection>

      <References references={article.references} />
      {measuredExperimentIds.has(article.id) ? (
        <Link
          className="button button-primary paper-next-link"
          to={`/blog/experiments/${article.id}`}
        >
          Read the measured study <ArrowRight />
        </Link>
      ) : null}
    </article>
  );
}

export function ExperimentPage() {
  const { gameId = "" } = useParams();
  const article = botArticleById.get(gameId);
  const results = experimentResults.filter((result) => result.game === gameId);
  if (!article || !measuredExperimentIds.has(gameId) || results.length === 0)
    return <BlogMissing />;

  const totalGames = results.reduce((sum, result) => sum + result.games, 0);
  const policyKeys = Array.from(
    new Set([
      ...Object.keys(experimentPolicyContexts[article.id] ?? {}),
      ...results.flatMap((result) => [result.profileA, result.profileB]),
    ]),
  );
  return (
    <article className="blog-page blog-article research-paper" style={articleStyle(article)}>
      <PaperHeader eyebrow="CONTROLLED BOT STUDY" title={`${article.name}: policy comparison`}>
        This report compares {results.length} pre-defined policy match-ups across{" "}
        {totalGames.toLocaleString()} complete or bounded games. It describes the game, hypotheses,
        controls, uncertainty, results, and limits so a percentage is never separated from how it
        was obtained.
      </PaperHeader>

      <aside className="paper-meta" aria-label="Study summary">
        <span>
          <b>{results.length}</b> comparisons
        </span>
        <span>
          <b>{totalGames.toLocaleString()}</b> games
        </span>
        <span>
          <b>95%</b> Wilson intervals
        </span>
        <span>
          <b>Balanced</b> first position
        </span>
      </aside>

      <PaperSection number="01" title="Abstract and research question" icon={<FlaskConical />}>
        <p>{article.abstract}</p>
        <p>
          The primary question is whether policy A changes win probability relative to policy B
          under the same production rules. The null hypothesis for each comparison is that neither
          policy has a win-probability advantage after balancing first position. Draws remain their
          own outcome and are not silently counted as half-wins.
        </p>
      </PaperSection>

      <PaperSection number="02" title="Game and policy definitions" icon={<BookOpen />}>
        <GameDiagram gameId={article.id} />
        <ol className="rule-list">
          {article.gameWorks.map((rule) => (
            <li key={rule}>{rule}</li>
          ))}
        </ol>
        <CitationLine references={article.references} />
        <div className="profile-grid compact-profiles">
          {article.profiles.map((profile) => (
            <article className="profile-card" key={profile.name}>
              <h3>{profile.name}</h3>
              <p>{profile.behaviour}</p>
            </article>
          ))}
        </div>
      </PaperSection>

      <PaperSection number="03" title="Experimental design and controls" icon={<Scale />}>
        <ResearchMethod compact />
        <div className="control-grid">
          <div>
            <b>Unit of analysis</b>
            <span>One completed or pre-declared capped game.</span>
          </div>
          <div>
            <b>Independent variable</b>
            <span>The action-selection policy assigned to each seat.</span>
          </div>
          <div>
            <b>Primary outcome</b>
            <span>Win, loss, or draw for policy A.</span>
          </div>
          <div>
            <b>Rules path</b>
            <span>The same production reducer and legal-action validation used in rooms.</span>
          </div>
        </div>
        <ul>
          {Array.from(new Set(results.flatMap((result) => result.notes))).map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </PaperSection>

      <PaperSection number="04" title="Metrics and uncertainty" icon={<Sigma />}>
        <div className="equation-stack two-column-equations">
          <article className="equation-card">
            <h3>Observed win probability</h3>
            <div className="formula" role="math">
              p̂ = w / n
            </div>
            <p>
              w is wins by the named policy and n is all games in that comparison, including draws.
            </p>
          </article>
          <article className="equation-card">
            <h3>95% Wilson interval</h3>
            <div className="formula formula-small" role="math">
              (p̂ + z²/2n ± z√(p̂(1−p̂)/n + z²/4n²)) / (1 + z²/n)
            </div>
            <p>
              z = 1.96. Wilson intervals remain meaningful near 0% and 100%, where a simple normal
              interval performs poorly.
            </p>
          </article>
        </div>
        <p>
          An interval is not the probability that the true value lies inside this particular range.
          It is a procedure that would cover the underlying probability in about 95% of repeated
          studies under its assumptions. Game outcomes within paired conditions can still be
          related, so the intervals are descriptive rather than a complete paired causal model.
        </p>
      </PaperSection>

      <PaperSection number="05" title="Results" icon={<BarChart3 />}>
        <section className="result-policy-key">
          <h3 className="result-policy-key-title">What each policy means for this game</h3>
          {policyKeys.map((policy) => (
            <article key={policy}>
              <h3>{profileLabel(policy)}</h3>
              <p>
                {experimentPolicyContexts[article.id]?.[policy] ??
                  `This label identifies the ${profileLabel(policy).toLowerCase()} action-selection policy used in this study.`}
              </p>
            </article>
          ))}
        </section>
        <div className="experiment-grid">
          {results.map((result) => (
            <ResultFigure result={result} key={`${result.profileA}-${result.profileB}`} />
          ))}
        </div>
        <ResultTable results={results} />
      </PaperSection>

      <PaperSection number="06" title="Interpretation" icon={<CheckCircle2 />}>
        <div className="finding-grid">
          {results.map((result) => (
            <article key={`${result.profileA}-${result.profileB}`}>
              <h3>
                {profileLabel(result.profileA)} vs {profileLabel(result.profileB)}
              </h3>
              <p>{interpretResult(result)}</p>
            </article>
          ))}
        </div>
        <p className="caution-note">
          Statistical separation is not the same as practical importance. These results describe
          these named implementations, rules, caps, and opponents. They do not rank every possible
          aggressive, defensive, or strategic bot.
        </p>
      </PaperSection>

      <PaperSection number="07" title="Threats to validity and future work" icon={<ShieldCheck />}>
        <ul className="limitation-list">
          {article.limitations.map((limitation) => (
            <li key={limitation}>{limitation}</li>
          ))}
          <li>
            Policy parameters were fixed for this study; tuning on these same outcomes would overfit
            the evaluation.
          </li>
          <li>
            Wilson intervals quantify binomial sampling uncertainty but do not include software
            defects, alternative rule sets, or deployment latency.
          </li>
          <li>
            Future work should add independent holdout openings or fleets and paired-outcome
            analysis without replacing the published comparison.
          </li>
        </ul>
      </PaperSection>

      <References
        references={[
          ...article.references.filter(
            (reference) => !reference.href.startsWith("/blog/experiments"),
          ),
          {
            label:
              "Agarwal et al., Deep Reinforcement Learning at the Edge of the Statistical Precipice",
            href: "https://papers.neurips.cc/paper_files/paper/2021/hash/f514cec81cb148559cf475e7426eed5e-Abstract.html",
          },
          {
            label: "Wilson, Probable Inference, the Law of Succession, and Statistical Inference",
            href: "https://doi.org/10.1080/01621459.1927.10502953",
          },
          {
            label: "Sturgul, Common Random Numbers in Simulation Experiments",
            href: "https://doi.org/10.1016/0191-2615(92)90031-Q",
          },
        ]}
      />
    </article>
  );
}

export function BotExperimentsPage() {
  return (
    <article className="blog-page blog-article research-paper">
      <PaperHeader
        eyebrow="TOPZLE LAB METHOD"
        title="How we compare bots without fooling ourselves"
      >
        Repetition matters, but design matters first. A large biased study only estimates the bias
        more precisely.
      </PaperHeader>
      <PaperSection number="01" title="The comparison protocol" icon={<FlaskConical />}>
        <ResearchMethod />
      </PaperSection>
      <PaperSection number="02" title="Why each control exists" icon={<Scale />}>
        <div className="term-grid">
          <div className="term-card">
            <dfn>Seat balancing</dfn>
            <p>
              Alternating first position prevents a policy from inheriting every first-move
              advantage.
            </p>
          </div>
          <div className="term-card">
            <dfn>Common random conditions</dfn>
            <p>
              Where practical, paired openings or fleets reduce noise caused by one policy receiving
              easier circumstances.
            </p>
          </div>
          <div className="term-card">
            <dfn>Fixed stopping rule</dfn>
            <p>
              A declared sample size and cap prevent stopping only when a preferred result appears.
            </p>
          </div>
          <div className="term-card">
            <dfn>Uncertainty interval</dfn>
            <p>A range communicates sampling precision that a standalone percentage hides.</p>
          </div>
        </div>
      </PaperSection>
      <References
        references={[
          {
            label: "Agarwal et al., robust and statistically reliable agent evaluation",
            href: "https://papers.neurips.cc/paper_files/paper/2021/hash/f514cec81cb148559cf475e7426eed5e-Abstract.html",
          },
          {
            label: "Wilson score interval, original paper",
            href: "https://doi.org/10.1080/01621459.1927.10502953",
          },
          {
            label: "Common random numbers in simulation comparisons",
            href: "https://doi.org/10.1016/0191-2615(92)90031-Q",
          },
        ]}
      />
    </article>
  );
}

function PaperHeader({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <header className="blog-hero compact paper-header">
      <span className="eyebrow">{eyebrow}</span>
      <h1>{title}</h1>
      <p>{children}</p>
    </header>
  );
}

function PaperSection({
  number,
  title,
  icon,
  children,
}: {
  number: string;
  title: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="paper-section">
      <header>
        <span>{number}</span>
        <div className="paper-section-icon">{icon}</div>
        <h2>{title}</h2>
      </header>
      {children}
    </section>
  );
}

function DecisionPipeline() {
  return (
    <figure className="decision-pipeline" aria-label="Bot decision pipeline">
      {[
        ["Observe", "Trusted game state"],
        ["Validate", "Legal actions only"],
        ["Evaluate", "Score or search"],
        ["Choose", "One action"],
        ["Reduce", "Production rules"],
      ].map(([title, subtitle], index) => (
        <div key={title} className="pipeline-step">
          <span>{index + 1}</span>
          <b>{title}</b>
          <small>{subtitle}</small>
        </div>
      ))}
    </figure>
  );
}

function GameDiagram({ gameId }: { gameId: string }) {
  if (gameId === "tic-tac-toe")
    return (
      <figure className="game-diagram ttt-diagram" aria-label="Tic Tac Toe game tree">
        <div
          className="mini-board"
          role="img"
          aria-label="Tic Tac Toe position where X can complete a diagonal"
        >
          {ticTacToeDiagramCells.map((cell) => (
            <span className={cell.tone} key={cell.id}>
              {cell.mark}
            </span>
          ))}
        </div>
        <ArrowRight />
        <div className="branch-labels">
          <b>
            <i className="win" /> Win <em>+1</em>
          </b>
          <b>
            <i className="draw" /> Draw <em>0</em>
          </b>
          <b>
            <i className="loss" /> Loss <em>−1</em>
          </b>
        </div>
        <figcaption>
          X can complete the highlighted diagonal now. Minimax follows every legal branch to a win,
          draw, or loss value.
        </figcaption>
      </figure>
    );
  if (gameId === "chess")
    return (
      <figure className="game-diagram tree-diagram" aria-label="Chess search tree">
        <div className="tree-root">position</div>
        <div className="tree-row">
          <span>capture</span>
          <span>castle</span>
          <span>develop</span>
        </div>
        <div className="tree-row leaves">
          <span>+1.8</span>
          <span>+2.1</span>
          <span>+1.3</span>
          <span>−0.7</span>
          <span>+0.9</span>
        </div>
        <figcaption>Candidate moves are compared after legal opponent replies.</figcaption>
      </figure>
    );
  if (gameId === "ludo")
    return (
      <figure className="game-diagram ludo-diagram" aria-label="Ludo move scoring">
        <div className="dice-face">4</div>
        <div className="utility-bars">
          <span style={{ width: "92%" }}>capture 92</span>
          <span style={{ width: "72%" }}>safe square 72</span>
          <span style={{ width: "48%" }}>progress 48</span>
        </div>
        <figcaption>
          One roll can create several legal goti choices with different utilities.
        </figcaption>
      </figure>
    );
  if (gameId === "connect-four")
    return (
      <figure className="game-diagram connect-diagram" aria-label="Connect Four threat window">
        <div className="connect-grid">
          {connectDiagramCells.map((cell) => (
            <i className={cell.tone} key={cell.id} />
          ))}
        </div>
        <div className="diagram-callout">playable three →</div>
        <figcaption>Gravity determines whether an empty winning cell is playable now.</figcaption>
      </figure>
    );
  if (gameId === "battleship")
    return (
      <figure
        className="game-diagram heatmap-diagram"
        aria-label="Battleship probability density heatmap"
      >
        <div className="heatmap-grid">
          {battleshipDiagramCells.map((cell) => (
            <i
              style={{ opacity: cell.opacity }}
              className={cell.target ? "target" : ""}
              key={cell.id}
            />
          ))}
        </div>
        <div className="heat-legend">
          <span>fewer placements</span>
          <span>more placements</span>
        </div>
        <figcaption>
          The brightest legal cell belongs to the most remaining ship placements.
        </figcaption>
      </figure>
    );
  if (gameId === "draw-guess")
    return (
      <figure
        className="game-diagram classifier-diagram"
        aria-label="Drawing classification pipeline"
      >
        <div className="sketch">✎</div>
        <ArrowRight />
        <div className="pixels">28 × 28</div>
        <ArrowRight />
        <div className="predictions">
          <span>cat 0.71</span>
          <span>owl 0.18</span>
          <span>cup 0.06</span>
        </div>
        <figcaption>Strokes become pixels, features, then ranked labels on the device.</figcaption>
      </figure>
    );
  if (gameId === "rock-paper-scissors")
    return (
      <figure className="game-diagram rps-diagram" aria-label="Rock Paper Scissors payoff cycle">
        <span>✊</span>
        <b>beats</b>
        <span>✌️</span>
        <b>beats</b>
        <span>✋</span>
        <b>beats rock</b>
        <figcaption>The uniform mixed strategy assigns one third to every gesture.</figcaption>
      </figure>
    );
  return (
    <figure
      className="game-diagram markov-diagram"
      aria-label="Snakes and Ladders Markov transitions"
    >
      <div className="markov-state">20</div>
      <div className="markov-arrows">
        → 21
        <br />→ 22
        <br />→ 23 ⇡ 46
        <br />→ 24
        <br />→ 25
        <br />→ 26
      </div>
      <figcaption>Each fair die outcome transfers probability to a destination square.</figcaption>
    </figure>
  );
}

function ResultFigure({ result }: { result: ExperimentResult }) {
  const metrics = Object.entries(result.metrics ?? {}).filter(([, value]) =>
    Number.isFinite(value),
  );
  return (
    <figure className="experiment-card">
      <figcaption>
        <BarChart3 />
        <h3>
          {profileLabel(result.profileA)} vs {profileLabel(result.profileB)}
        </h3>
        <span>{result.games.toLocaleString()} games</span>
      </figcaption>
      <div
        className="outcome-bar"
        role="img"
        aria-label={`${percent(result.winRateA)} ${result.profileA} wins, ${percent(result.drawRate)} draws, ${percent(result.winRateB)} ${result.profileB} wins`}
      >
        <i
          className="wins-a"
          style={{ width: percent(result.winRateA), background: policyColor(result.profileA) }}
        />
        <i className="draws" style={{ width: percent(result.drawRate) }} />
        <i
          className="wins-b"
          style={{ width: percent(result.winRateB), background: policyColor(result.profileB) }}
        />
      </div>
      <div className="outcome-legend">
        <span>
          <i className="wins-a" style={{ background: policyColor(result.profileA) }} />
          {profileLabel(result.profileA)} <b>{percent(result.winRateA)}</b>
        </span>
        <span>
          <i className="draws" />
          Draw <b>{percent(result.drawRate)}</b>
        </span>
        <span>
          <i className="wins-b" style={{ background: policyColor(result.profileB) }} />
          {profileLabel(result.profileB)} <b>{percent(result.winRateB)}</b>
        </span>
      </div>
      <ConfidenceInterval result={result} />
      {metrics.length ? (
        <div className="metric-strip">
          {metrics.slice(0, 4).map(([key, value]) => (
            <span key={key}>
              <small>{metricLabel(key)}</small>
              <b>{metricValue(key, value)}</b>
            </span>
          ))}
        </div>
      ) : null}
    </figure>
  );
}

function ConfidenceInterval({ result }: { result: ExperimentResult }) {
  const left = result.winRateA95[0] * 100;
  const width = Math.max(0.8, (result.winRateA95[1] - result.winRateA95[0]) * 100);
  return (
    <div className="ci-chart">
      <span>95% interval for {profileLabel(result.profileA)} wins</span>
      <div>
        <i style={{ left: `${left}%`, width: `${width}%` }} />
        <b style={{ left: `${result.winRateA * 100}%` }} />
      </div>
      <small>
        {percent(result.winRateA95[0])} to {percent(result.winRateA95[1])}
      </small>
    </div>
  );
}

function ResultTable({ results }: { results: ExperimentResult[] }) {
  return (
    <div className="result-table-wrap">
      <table className="result-table">
        <thead>
          <tr>
            <th>Comparison</th>
            <th>n</th>
            <th>A wins</th>
            <th>Draws</th>
            <th>B wins</th>
            <th>A 95% CI</th>
          </tr>
        </thead>
        <tbody>
          {results.map((result) => (
            <tr key={`${result.profileA}-${result.profileB}`}>
              <td>
                {profileLabel(result.profileA)} vs {profileLabel(result.profileB)}
              </td>
              <td>{result.games.toLocaleString()}</td>
              <td>
                {result.winsA.toLocaleString()} ({percent(result.winRateA)})
              </td>
              <td>
                {result.draws.toLocaleString()} ({percent(result.drawRate)})
              </td>
              <td>
                {result.winsB.toLocaleString()} ({percent(result.winRateB)})
              </td>
              <td>
                {percent(result.winRateA95[0])}–{percent(result.winRateA95[1])}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function metricLabel(key: string) {
  return key.replace(/([A-Z])/g, " $1").replace(/^./, (character) => character.toUpperCase());
}

function metricValue(key: string, value: number) {
  if (key.toLowerCase().includes("accuracy")) return percent(value);
  return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2);
}

function interpretResult(result: ExperimentResult) {
  const difference = result.winRateA - result.winRateB;
  if (result.drawRate > 0.95)
    return `Almost every game was drawn. The observed data do not support a practical win-rate advantage for either named policy in this match-up.`;
  if (result.winRateA95[0] > result.winRateB95[1])
    return `${profileLabel(result.profileA)} had the higher observed win probability by ${percent(Math.abs(difference))}. Its separate 95% win interval sits above the interval for ${profileLabel(result.profileB)} in this study.`;
  if (result.winRateB95[0] > result.winRateA95[1])
    return `${profileLabel(result.profileB)} had the higher observed win probability by ${percent(Math.abs(difference))}. Its separate 95% win interval sits above the interval for ${profileLabel(result.profileA)} in this study.`;
  return `The observed win-rate difference was ${percent(Math.abs(difference))}, but the separate 95% intervals overlap. Treat this comparison as inconclusive rather than declaring the larger point estimate a reliable advantage.`;
}

function ResearchMethod({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`research-method ${compact ? "compact" : ""}`}>
      <ol>
        <li>
          <b>Pre-specify.</b> Fix policies, sample sizes, game rules, random schedule, and safety
          caps before comparing outcomes.
        </li>
        <li>
          <b>Balance.</b> Reverse first-player position or colour and pair random conditions where
          the game permits it.
        </li>
        <li>
          <b>Validate.</b> Pass every action through the production legal-action and
          state-transition path.
        </li>
        <li>
          <b>Measure completely.</b> Keep wins, losses, draws, capped games, and game-specific
          efficiency metrics.
        </li>
        <li>
          <b>Report uncertainty.</b> Show the sample size and two-sided 95% Wilson interval beside
          every point estimate.
        </li>
      </ol>
    </div>
  );
}

function References({ references }: { references: Array<{ label: string; href: string }> }) {
  return (
    <section className="paper-section references">
      <header>
        <span>REF</span>
        <div className="paper-section-icon">
          <BookOpen />
        </div>
        <h2>References and further reading</h2>
      </header>
      <ol>
        {references.map((reference) => (
          <li key={reference.href}>
            <a
              href={reference.href}
              target={reference.href.startsWith("http") ? "_blank" : undefined}
              rel={reference.href.startsWith("http") ? "noreferrer" : undefined}
            >
              {reference.label}
            </a>
          </li>
        ))}
      </ol>
    </section>
  );
}

function CitationLine({ references }: { references: Array<{ label: string; href: string }> }) {
  const publicReferences = references.filter(
    (reference) => !reference.href.startsWith("/blog/experiments"),
  );
  if (publicReferences.length === 0) return null;
  return (
    <p className="inline-citations">
      Sources:{" "}
      {publicReferences.map((reference, index) => (
        <span key={reference.href}>
          {index > 0 ? " " : ""}
          <a
            href={reference.href}
            target={reference.href.startsWith("http") ? "_blank" : undefined}
            rel={reference.href.startsWith("http") ? "noreferrer" : undefined}
            aria-label={reference.label}
          >
            [{index + 1}]
          </a>
        </span>
      ))}
    </p>
  );
}

export function SmartLudoDicePage() {
  return (
    <article
      className="blog-page blog-article research-paper"
      style={{ "--lab-accent": "#2ed69b", "--lab-accent-2": "#ffc247" } as CSSProperties}
    >
      <PaperHeader
        eyebrow="LUDO RULES & FAIRNESS"
        title="Smart Dice is competitive mode, not a fair die"
      >
        Topzle labels the mode in the room. Fair Dice assigns every face probability 1/6. Smart Dice
        keeps every face possible but may adjust positive weights using the disclosed game
        situation.
      </PaperHeader>
      <PaperSection number="01" title="Probability model" icon={<Sigma />}>
        <div className="equation-card">
          <div className="formula">P(D = k) = wₖ / Σᵢ₌₁⁶ wᵢ</div>
          <p>
            When all gotis are in the yard, weights 4, 4, 4, 4, 4, 10 give P(6) = 10/30 = 1/3 and
            P(each other face) = 4/30 = 2/15.
          </p>
        </div>
      </PaperSection>
      <PaperSection number="02" title="Tactical and catch-up weighting" icon={<Scale />}>
        <p>
          An exact capture face receives the largest tactical bonus. Adjacent faces receive smaller
          bonuses, so needing 4 to capture can lift 3, 4, and 5. Exact home rolls and other legal
          movement receive modest bonuses. A capped multiplier strengthens bonuses for a trailing
          player and weakens them for a clear leader.
        </p>
      </PaperSection>
      <PaperSection number="03" title="Disclosure, control, and audit" icon={<ShieldCheck />}>
        <p>
          Administrators can disable Smart Dice. Existing rooms switch to equal weights on their
          next roll. Each roll history records the dice mode and six weights, allowing the server
          decision to be inspected. A weighted roll is never labelled fair.
        </p>
      </PaperSection>
    </article>
  );
}

function BlogMissing() {
  return (
    <section className="blog-page research-pending">
      <h1>That lab note is not available.</h1>
      <p>Experiment reports appear only for studies that were actually completed.</p>
      <Link to="/blog">Back to Topzle Lab</Link>
    </section>
  );
}
